import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './PrescriptionMedicamentSelectItem.module.scss';
import { PrescriptionMedicament } from '../../../graphql/graphql';
import useTranslation from '../../../Hooks/useTranslation';
import { joinClasses } from '../../../Utils/className.util';

interface ContentItem {
	title: string;
	value: string | number | boolean;
	valueDetail?: string | number | boolean;
}

interface Icon {
	src: string;
	alt: string;
}

const PrescriptionMedicamentSelectItem: React.FC<any> = props => {
	const { data, selectOption } = props;

	const [contentItems, setContentItems] = useState<ContentItem[]>([]);
	const [icons, setIcons] = useState<Icon[]>([]);
	const [customBackground, setCustomBackground] = useState<string | null>(null);
	const [customTitleClass, setCustomTitleClass] = useState<string | null>(null);
	const { item }: { item: PrescriptionMedicament } = data;
	const { t } = useTranslation();

	useEffect(() => {
		setCustomBackground(null);
		setCustomTitleClass(null);
		let tempContentItems: ContentItem[] = [];
		let tempIcons: Icon[] = [];
		if (item.code) {
			tempContentItems.push({
				title: 'component.ePrescription.code',
				value: item.code,
			});
		}
		if (item.form) {
			tempContentItems.push({
				title: 'component.ePrescription.form',
				value: item.form,
			});
		}
		if (item.strength) {
			tempContentItems.push({
				title: 'component.ePrescription.strength',
				value: item.strength,
			});
		}
		if (item.packaging) {
			tempContentItems.push({
				title: 'component.ePrescription.packaging',
				value: item.packaging,
			});
		}
		if (item.priceInsuranceCompanyRawFormatted) {
			tempContentItems.push({
				title: 'component.ePrescription.coveredByInsurance',
				value: item.priceInsuranceCompanyRawFormatted,
			});
		}
		if (item.priceExtraFormatted) {
			tempContentItems.push({
				title: 'component.ePrescription.patientSupplement',
				value: item.priceExtraFormatted,
			});
		}
		if (item.priceRawFormatted) {
			tempContentItems.push({
				title: 'component.ePrescription.priceFinal',
				value: item.priceRawFormatted,
			});
		}
		if (item.lim) {
			tempContentItems.push({
				title: 'component.ePrescription.lim',
				value: item.lim,
			});
		}

		// LIMITATIONS
		if (item.hasIndicationLimitations && !item.hasSpecialityLimitations) {
			tempContentItems.push({
				title: 'component.ePrescription.limitations',
				value: t('component.ePrescription.indication') ?? '',
			});
		}
		if (item.hasSpecialityLimitations && !item.hasIndicationLimitations) {
			tempContentItems.push({
				title: 'component.ePrescription.limitations',
				value: t('component.ePrescription.speciality') ?? '',
				valueDetail: item.specialityLimitations ?? '',
			});
		}
		if (item.hasSpecialityLimitations && item.hasIndicationLimitations) {
			tempContentItems.push({
				title: 'component.ePrescription.limitations',
				value:
					t('component.ePrescription.indication') +
						', ' +
						t('component.ePrescription.speciality') ?? '',
				valueDetail: item.specialityLimitations ?? '',
			});
		}

		//DOPING
		if (item.doping) {
			tempContentItems.push({
				title: 'component.ePrescription.doping',
				value: item.doping,
			});
		}

		if (item.dispensing) {
			tempContentItems.push({
				title: 'component.ePrescription.issue',
				value: item.dispensing,
			});

			switch (item.dispensing) {
				case 'L':
				case 'R':
					setCustomBackground('#f5fee4');
					tempContentItems.push({
						title: '',
						value: t('component.ePrescription.paidFromInsurance') ?? '',
					});
					break;
				case 'F':
				case 'O':
				case 'P':
				case 'V':
					tempContentItems.push({
						title: '',
						value: t('component.ePrescription.paidByPatient') ?? '',
					});
					break;
			}
		}

		//STYLING
		if (item.rationalPrescription) {
			switch (item.rationalPrescription) {
				case 'positive_sheet':
					tempIcons.push({
						src: '/dist/icons/vzp-icon.svg',
						alt: 'vzp icon',
					});
					break;
				case 'preferred_drug':
					setCustomTitleClass('green');
					break;
				case 'not_recommended_drug':
					setCustomTitleClass('yellow');
					break;
				case 'expensive_medicine':
					setCustomTitleClass('red');
					break;
			}
		}
		if (item.hasLimitations) {
			tempIcons.push({
				src: '/dist/icons/warning-triangle.svg',
				alt: 'warning triangle',
			});
		}
		if (item.doping) {
			tempIcons.push({
				src: '/dist/icons/doping-icon.svg',
				alt: 'doping icon',
			});
		}

		setIcons(tempIcons);
		setContentItems(tempContentItems);
	}, [item]);

	const handleSelect = () => {
		selectOption({
			id: item.id,
			label: item.name,
			item: item,
		});
	};

	return (
		<div
			className={styles.wrapper}
			style={customBackground ? { backgroundColor: customBackground } : {}}
			onClick={handleSelect}
		>
			<div className={styles.header}>
				<div className={joinClasses([styles.title, styles[customTitleClass]])}>
					{item.name}
				</div>
				<div className={styles.icons}>
					{icons.map(({ src, alt }, index) => {
						return <img src={src} alt={alt} key={index} />;
					})}
				</div>
			</div>
			<div className={styles.content}>
				{contentItems.map(({ title, value, valueDetail }, index) => {
					return (
						<div key={index}>
							{title && (
								<>
									<span>{t(title)}</span> -{' '}
								</>
							)}
							{value && value}
							{valueDetail && ': ' + valueDetail}
							{index < contentItems.length - 1 && <span>,</span>}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default PrescriptionMedicamentSelectItem;
